// The PG&E thermal console — the shell's first instance.
//
// FOR A CLIENT WITH NO VESSELS. The fleet console arms a vessel × operation
// limit set and grades everything against it. There is nothing here to arm and
// nothing to sail. What this client needs is the daily PEAK intake temperature
// against the threshold their licence is written to, far enough ahead to
// schedule a second heat exchanger rather than scramble one.
//
// WHAT ARMS THE VERDICT: threshold × lead. Both are real controls over real
// data — the sweep scores all six thresholds with a byte-identical model, and
// the band and blind error are published per lead — so moving either moves the
// margin, the band and the tier TOGETHER. A control that renders but changes
// nothing would be worse than no control.
//
// THE LEAD GOES TO NINE, which is where the data actually stops. Les asked for
// ten; Open-Meteo's ten-day product counts today, so nine is the honest number
// and the tenth would be fabricated.
//
// Every position has a forecast behind it, which was not true until recently:
// two observation-only station columns were carrying three days and silently
// truncating the whole forecast, so a 1–7 control would have offered four dead
// positions. See verify/frame.py in the seatemp project.
//
// The far days cost real accuracy and the console SHOWS that rather than hiding
// it — the confidence strip is banded on the measured blind error per lead, so
// arming day 9 makes its own price visible.
//
// THE BANNER PRINTS THE AGE OF THE PLANT'S OWN RECORD. Since the 2010-2026
// export arrived (2026-10-03) that is typically 2-3 days, not the 13-37 it once
// was; the bar turns calm when it is under a week. A console that draws the
// plant's trace without printing its age implies a liveness it may not have.
//
// THE VERDICT LEADS WITH A RANGE, not a number (2026-10-04). The point forecast
// is unbiased but sees half the hot days at five days; PG&E's own offset model
// already produces a single number. The envelope of three ocean models on one
// scale is the thing only this console can show.

import { createShell, type Shell } from "../shell/shell.js";
import { createTracks, type Tracks } from "./tracks.js";
import { createSiteMap, type SiteMap } from "./sitemap.js";
import { renderPanel } from "./panels.js";
import { feedHealth, worstStatus } from "./feeds.js";
import { type Payload, type SourcePoint, type ModelSpreadRow, num, f1, bandForLead, dayAtLead, tierFor, fBound, boundLabel, spreadAt, memberCount, scorecard } from "./payload.js";
import type { RailItem } from "../../ui/rail.js";
// The fleet console's own control. Same gesture, same markup, same styling —
// a client should not have to learn a second way to arm a console. It sits in
// the prediction strip here rather than in Site; see `build`.
import { segmented } from "../../ui/settings.js";

const THRESHOLDS = [61, 62, 63, 64, 65, 66];
// Nine, not ten. Open-Meteo's "10 day forecast" counts today, so the marine
// source gives NINE forward days and silently returns the same 239 hours for
// forecast_days of 10 through 16. A tenth position would point at a day with
// no forecast behind it — the exact fabrication that got the horizon cut from
// 14 to 7 in the first place.
const LEADS = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const BUOY = { lat: 35.204, lon: -120.859 };

/** The most recent OBSERVED buoy daily minimum, °F — the bulk-water reference
 *  the satellite field is printed against. Forward days are model output and
 *  would make the comparison circular. */
function latestBuoyMin(p: Payload): number | null {
  const past = (p.drivers ?? []).filter((d) => d.future !== true);
  for (let i = past.length - 1; i >= 0; i--) {
    const v = num(past[i]?.sst_f);
    if (v !== null) return v;
  }
  return null;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls !== undefined) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
}

const RAIL: RailItem[] = [
  { id: "site", label: "Site", icon: "site" },
  { id: "skill", label: "Skill", icon: "svp", title: "Blind walk-forward performance" },
  { id: "obs", label: "Observations", icon: "observations" },
  { id: "systems", label: "Systems", icon: "systems", title: "Source health and payload age" },
  { id: "feedback", label: "Feedback", icon: "feedback", title: "Tell us what is wrong or missing" },
];

export function mountThermalConsole(root: HTMLElement): void {
  document.title = "Console — SeaState";
  const loading = el("main", "page");
  loading.appendChild(el("p", "note", "Loading the panel…"));
  root.appendChild(loading);

  void fetch("/api/thermal", { credentials: "same-origin" })
    .then(async (res) => {
      if (!res.ok) {
        let msg = `The panel is not available (${res.status}).`;
        try {
          const body = (await res.json()) as { error?: string };
          if (typeof body.error === "string") msg = body.error;
        } catch {
          /* a non-JSON body is not worth a second failure */
        }
        throw new Error(msg);
      }
      return (await res.json()) as Payload;
    })
    .then((p) => {
      loading.remove();
      build(root, p);
    })
    .catch((err: unknown) => {
      loading.replaceChildren();
      const msg = err instanceof Error ? err.message : "The panel is not available.";
      loading.appendChild(el("h1", undefined, "Intake temperature"));
      loading.appendChild(el("p", "note", msg));
    });
}

function build(root: HTMLElement, p: Payload): void {
  // style.css scopes this console's height rules to `.thermalapp` — the body,
  // the rail and the workspace panel give back the banner's and the strip's
  // height — and nothing ever put the class on. The rules were dead, and the
  // page scrolled by exactly one banner (113 px once the strip landed). Found
  // 2026-10-09 by asking the page: `document.querySelector(".thermalapp")`
  // was null while `--thermal-bars` read 113px.
  root.classList.add("thermalapp");
  let threshold = num(p.thresholds?.action_f) ?? 64;
  let lead = 1;
  let tracks: Tracks | null = null;
  let map: SiteMap | null = null;
  let shell: Shell | null = null;
  // The marker the operator last clicked. Held here rather than inside the panel
  // so the panel can be rebuilt against it when the selection changes.
  let focus: SourcePoint | undefined;

  // WHERE THE PREDICTION LIVES: a strip across the top of the page, under the
  // banner and above the map — Les, 2026-10-09: "the prediction at the top".
  //
  // From 2026-10-04 to 2026-10-09 the verdict and its controls sat inside the
  // Site workspace, where the fleet console arms vessel × operation: same
  // place, same segmented control, one idiom for both clients. That was the
  // right argument for the knobs and the wrong one for the answer. The fleet
  // operator arms a vessel and then reads a map; this client opens the console
  // for one number and its margin, and that number was a panel away, below the
  // fold on a laptop, and gone when the panel was closed. The strip is the
  // first thing after the banner, full width, and the controls that arm it sit
  // in it — the number and what it is graded against are never apart.
  const verdict = el("div", "wsverdict");

  // THE BANNER SAYS WHAT IS ACTUALLY TRUE TODAY.
  //
  // It used to read "DEMO — NEED HOURLY FEED · the plant's hourly export is a
  // file, N days old. Live, it is worth 21 % at one day." Both halves were
  // obsolete by 2026-10-03:
  //
  //   the gap  PG&E supplied the 2010-2026 export, so the record runs 2-3 days
  //            behind rather than 37. "NEED HOURLY FEED" overstated the problem.
  //   the 21%  that was meter-vs-NO-meter. Measured properly
  //            (research/feed_cadence.py) a NIGHTLY file buys 4.5 % at one day
  //            and a live feed 23 %; past two days either is worth ~1 %. Quoting
  //            21 % for a nightly file was the wrong number for the wrong ask.
  //
  // What matters to a reader is how old the plant's own record is, so say that.
  const h = p.intake_hourly;
  const stale = h?.stale_days;
  const fresh = typeof stale === "number" && stale <= 7;
  const demo = el("div", fresh ? "demoflag demoflag-ok" : "demoflag");
  demo.appendChild(el("strong", undefined,
    h === null || h === undefined ? "NO PLANT RECORD" : "DEMO"));
  demo.appendChild(el("span", undefined,
    h === null || h === undefined
      ? " · the plant's own temperature record is not connected"
      : ` · plant record current to ${(h.last ?? "?").slice(0, 10)}` +
        ` (${stale ?? "?"} d). Forecast from public buoys and three ocean models.`));
  root.appendChild(demo);

  /** Recompute the verdict from the armed threshold and lead. Everything the
   *  reader acts on is derived here, in one place, so the tier can never
   *  disagree with the margin printed beside it. */
  const refresh = (): void => {
    verdict.replaceChildren();
    const day = dayAtLead(p, lead);
    const bound = num(day?.decision_f);
    const central = num(day?.forecast_f) ?? num(day?.estimate_f);
    const band = bandForLead(p, lead);
    const margin = bound === null ? null : threshold - bound;

    // The tier is computed from the BOUND, never the central estimate — the rule
    // lives in payload.ts so it is testable without a browser.
    const tier = tierFor(bound, threshold, p.decision?.reduced_in_window === true);

    const pill = el("span", `tier tier-${tier.toLowerCase()}`, tier);
    verdict.appendChild(pill);
    const stat = (k: string, v: string, n?: string): void => {
      const box = el("div", "armstat");
      box.appendChild(el("div", "armstat-v", v));
      box.appendChild(el("div", "armstat-l", k));
      if (n !== undefined) box.appendChild(el("div", "armstat-n", n));
      verdict.appendChild(box);
    };
    // The headline tile. `hot` paints it when the top of the range reaches the
    // armed threshold — colour as a second channel, never the only one.
    const statBig = (k: string, v: string, n: string, hot: boolean): void => {
      const box = el("div", hot ? "armstat armstat-big armstat-hot" : "armstat armstat-big");
      box.appendChild(el("div", "armstat-v", v));
      box.appendChild(el("div", "armstat-l", k));
      box.appendChild(el("div", "armstat-n", n));
      verdict.appendChild(box);
    };
    // THE RANGE LEADS. A single number is the one thing this product should not
    // lead with: the point forecast is unbiased but at five days it sees half
    // the hot days (research/why_warm_days.py), and PG&E's own offset model
    // already produces a single number. What they cannot produce is the width
    // between three ocean models on one scale. So the first thing printed is
    // the envelope for the armed day, with the point estimate inside it.
    const sp = spreadAt(p, day?.date);
    const rLo = sp?.lo_f ?? num(day?.lo_f);
    const rHi = sp?.hi_f ?? num(day?.hi_f);
    const rangeTxt = rLo === null || rHi === null || rLo === undefined || rHi === undefined
      ? "—" : `${rLo.toFixed(1)}–${rHi.toFixed(1)} °F`;
    const rangeNote = sp !== undefined
      ? `${memberCount(sp)}-model spread ${(sp.spread_f ?? 0).toFixed(1)} °F · ours ${f1(central)}`
      : `90 % band · ours ${f1(central)}`;
    statBig(`Range at ${lead} d`, rangeTxt, rangeNote,
      rHi !== null && rHi !== undefined && rHi >= threshold);
    // Margin inherits the bound's precision: a 0.03 °F margin printed as "0.0"
    // beside NORMAL raises the same contradiction from the other direction.
    stat(`Margin at ${lead} d`, margin === null ? "—" : `${margin.toFixed(2)} °F`,
      `to ${threshold.toFixed(0)} °F`);
    // MEASURED coverage, not the fitting parameter. This line used to read
    // "97.5th percentile" straight off `tau`, which is what the quantile is
    // fitted at, not what it delivers: blind walk-forward puts it at ~93.8th at
    // one day and ~91.3rd at nine. Widening it to earn the 97.5 was measured and
    // rejected — it lowers CSI at every lead — so the number is right and the
    // label was wrong. Falls back to naming tau as nominal if the scoring run
    // has not been done, rather than silently reasserting the old claim.
    stat("Decision bound", fBound(bound), boundLabel(p.decision, lead));
    // THE SCORECARD: the last sixty days, both methods against the measured
    // daily max on the same days. Beside the verdict because it is the one
    // thing on this panel PG&E can check against their own thermometer, and
    // because the point of the column is that this is not the offset model.
    const sc = scorecard(p.series ?? []);
    if (sc.n > 0) {
      stat("SeaState within 1 °F", `${sc.ours} of ${sc.n} d`,
        `RMSE ${sc.ours_rmse?.toFixed(2)} °F · last ${sc.n} measured days`);
      stat("Open-Meteo + offset within 1 °F", `${sc.pge} of ${sc.n} d`,
        `RMSE ${sc.pge_rmse?.toFixed(2)} °F · our reconstruction of PG&E's method`);
    }
    tracks?.setThreshold(threshold);
  };

  // THE STRIP. The verdict on the left, the arming controls on the right. Built
  // once, here, and never inside a workspace: a workspace is painted lazily on
  // its first select (shell.ts `paint`), and the strip has to exist before any
  // of them does. Appended before the shell so it sits above the map.
  const strip = el("section", "predstrip");
  strip.setAttribute("aria-label", "Prediction");
  const arm = el("div", "predstrip-arm");
  const trow = el("div", "settings-row");
  trow.append(el("span", "settings-label", "Threshold"), segmented(
    THRESHOLDS.map((v) => [String(v), `${v} °F`] as const),
    () => String(threshold),
    (v) => { threshold = Number(v); refresh(); },
    "Grade every surface against this threshold",
  ));
  const lrow = el("div", "settings-row");
  lrow.append(el("span", "settings-label", "Lead"), segmented(
    LEADS.map((v) => [String(v), `${v} d`] as const),
    () => String(lead),
    (v) => { lead = Number(v); refresh(); },
    "Which day ahead the verdict is about",
  ));
  arm.append(trow, lrow);
  strip.append(verdict, arm);
  root.appendChild(strip);

  // THE BODY IS SIZED TO WHAT IS LEFT. style.css takes `--thermal-bars` off the
  // viewport for the app body and the workspace panel, and nothing ever set
  // it: the banner's height was quietly overflowing the page by one banner.
  // Measured, not asserted — the strip wraps to two or three rows on a narrow
  // pane, and the banner to two lines on a phone.
  const bars = (): void => {
    document.documentElement.style.setProperty(
      "--thermal-bars", `${demo.offsetHeight + strip.offsetHeight}px`);
    // The map's box just changed under it; a map engine sizes its canvas once
    // and has to be told, exactly as the shell tells it when the panel closes.
    map?.update({});
  };
  const barWatch = new ResizeObserver(bars);
  barWatch.observe(demo);
  barWatch.observe(strip);
  bars();

  shell = createShell(root, {
    rail: RAIL,
    initial: "site",
    storageNamespace: "thermal",
    renderWorkspace: (id, into) =>
      renderPanel(id, into, {
        payload: p,
        threshold: () => threshold,
        lead: () => lead,
        focusSource: () => focus,
        onLayer: (id, on) => map?.setLayer(id, on),
      }),
    centre: (into) => {
      // NOT given the threshold: a satellite skin temperature graded against an
      // intake bulk-water limit is the conflation this whole product exists to
      // avoid. It gets the buoy's daily minimum instead, to state the gap.
      map = createSiteMap(into, {
        field: p.sst_field ?? null,
        bulkRef: latestBuoyMin(p),
        site: p.site,
        buoy: BUOY,
        sources: p.sources ?? [],
        // A click on a marker opens Observations on that station. The panel is
        // rebuilt rather than patched: it is a different station's record, not
        // an edit to the one already shown, and rebuilding is what keeps the
        // sparkline, the stats and the caption from disagreeing about which
        // station they describe.
        onPickSource: (s) => {
          focus = s;
          const into2 = shell?.rail.workspace("obs");
          if (into2 !== undefined) {
            into2.replaceChildren();
            renderPanel("obs", into2, {
              payload: p,
              threshold: () => threshold,
              lead: () => lead,
              focusSource: () => focus,
              onLayer: (id, on) => map?.setLayer(id, on),
            });
          }
          shell?.rail.select("obs");
          shell?.rail.setPanelOpen(true);
        },
      });
      return () => map?.destroy();
    },
    stack: (into) => {
      tracks = createTracks(into, p);
      return () => tracks?.destroy();
    },
    bottom: (into) => {
      buildConditions(into, p, () => threshold);
      return undefined;
    },
    onResize: () => map?.update({}),
  });

  refresh();

  // A BADGE ON THE RAIL, so a stale feed is visible without opening the panel.
  //
  // rail.ts documents badges as the thing that turns navigation into a status
  // column, and this is exactly that case: the console reads perfectly normal
  // when a source has quietly stopped advancing, because every number on it is
  // computed from the last thing that source said. The whole point of Systems is
  // that nobody would think to look.
  const feeds = feedHealth(p, p.today ?? new Date().toISOString().slice(0, 10));
  const worst = worstStatus(feeds);
  if (worst !== "ok") {
    const bad = feeds.filter((f) => f.status !== "ok").map((f) => f.label);
    shell.rail.setBadge("systems", {
      level: worst === "stale" ? "stop" : "warn",
      title: `${bad.join(", ")} — see Systems`,
    });
  }

  // Expose what the render check needs. The temperature chart once drew its axes
  // and both threshold lines perfectly while plotting NOTHING, and it was found
  // by counting colours in the canvas rather than by looking at it. A panel that
  // can be asked what it drew does not need that again.
  interface ThermalProbe {
    tracks: number;
    cells: number;
    threshold: number;
    lead: number;
    tier: string;
    /** null before the map's style is ready — see SiteMap.reach. */
    reach: ReturnType<SiteMap["reach"]> | null;
  }
  (window as unknown as { __thermal?: () => ThermalProbe }).__thermal = () => ({
    tracks: tracks?.drawnSeries() ?? 0,
    cells: map?.paintedCells() ?? 0,
    threshold,
    lead,
    tier: verdict.querySelector(".tier")?.textContent ?? "",
    reach: (() => {
      try {
        return map?.reach() ?? null;
      } catch {
        return null; // style not loaded yet; the probe must never throw
      }
    })(),
  });

  window.addEventListener("beforeunload", () => {
    barWatch.disconnect();
    shell?.destroy();
  }, { once: true });
}

/** The bottom row: the forward window as a table, every cell printing its
 *  number, heat-graded against the armed threshold.
 *
 *  This is panel/heat.ts's LIMIT scale applied to a different limit. The fleet
 *  console grades against an armed vessel's wind and wave limits; a threshold in
 *  °F is the same kind of thing, which is why the bottom row needed no new idea
 *  — only a different number to grade against. */
function buildConditions(into: HTMLElement, p: Payload, threshold: () => number): void {
  const t = threshold();
  const days = (p.series ?? []).filter((d) => (d.date ?? "") >= (p.today ?? ""));
  const wrap = el("div", "ctable-in");
  const tbl = el("table", "cgrid");
  const thead = el("thead");
  const htr = el("tr");
  htr.appendChild(el("th", undefined, ""));
  for (const d of days) htr.appendChild(el("th", undefined, (d.date ?? "").slice(5)));
  thead.appendChild(htr);
  const tb = el("tbody");

  const line = (label: string, pick: (d: typeof days[number]) => number | null,
                grade: boolean): void => {
    const tr = el("tr");
    tr.appendChild(el("th", undefined, label));
    for (const d of days) {
      const v = pick(d);
      const td = el("td", undefined, v === null ? "—" : v.toFixed(1));
      if (grade && v !== null) {
        // Margin drives the fill; the number is always printed, so losing the
        // colour to a projector or a colour-blind reader costs nothing.
        const m = t - v;
        td.classList.add(m <= 0 ? "h4" : m <= 1 ? "h3" : m <= 3 ? "h2" : m <= 5 ? "h1" : "h0");
      }
      tr.appendChild(td);
    }
    tb.appendChild(tr);
  };

  // FOUR ROWS, RANGE-SHAPED. Was five (bound, forecast, band lo, band hi,
  // margin) — three of which restated the others. Now: the top of the model
  // envelope, our forecast, the bottom of the envelope, and the bound. The
  // reader's eye runs top-to-bottom through hot → ours → cool, and the fill
  // says at a glance which days any model puts over the line.
  const spread = (d: typeof days[number]): ModelSpreadRow | undefined => spreadAt(p, d.date);
  line("Range high °F", (d) => spread(d)?.hi_f ?? num(d.hi_f), true);
  line("Forecast °F", (d) => num(d.forecast_f) ?? num(d.estimate_f), true);
  line("Range low °F", (d) => spread(d)?.lo_f ?? num(d.lo_f), true);
  line("Decision bound °F", (d) => num(d.decision_f), true);

  tbl.append(thead, tb);
  wrap.appendChild(tbl);
  // Counted from the rows, not asserted: HYCOM's run ends two days before the
  // others', and "three" over a day that had two would be the console lying.
  const nModels = Math.max(0, ...days.map((d) => memberCount(spread(d))));
  wrap.appendChild(el("p", "note",
    `Range is the envelope of ${nModels >= 2 ? `${nModels} ocean models (HYCOM, CMEMS, Open-Meteo), each ` : "the 90 % band, "}` +
    `corrected onto the intake daily max. Graded against the armed ${t.toFixed(0)} °F threshold; every cell prints its number.`));
  into.appendChild(wrap);
}
